/**
 * Mitglieder-Profil — hier hinterlegte Daten füllen das Buchungs-Formular
 * automatisch vor (Name, Telefon, Adresse, Firma).
 */

import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { Button } from "@/components/Button";
import { useMe, useMemberToken } from "@/hooks/useMemberAuth";
import { ApiError, memberApi } from "@/lib/api";
import type { MemberProfile, MemberUpdateRequest } from "@/types/api";

const schema = z.object({
  full_name: z.string().min(2, "Bitte vollständigen Namen eintragen"),
  phone: z.string().max(64, "Max. 64 Zeichen").optional().or(z.literal("")),
  street: z.string().max(255).optional().or(z.literal("")),
  postal_code: z.string().max(16).optional().or(z.literal("")),
  city: z.string().max(128).optional().or(z.literal("")),
  country: z.string().max(2).optional().or(z.literal("")),
  company_name: z.string().max(255).optional().or(z.literal("")),
  marketing_opt_in: z.boolean(),
});
type Values = z.infer<typeof schema>;

const labelCls = "block text-body-sm font-medium text-ink";
const fieldCls =
  "mt-1 block w-full rounded border border-line bg-surface px-3 py-2 text-body-sm text-ink shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/30 focus:outline-none";
const readOnlyCls =
  "mt-1 block w-full rounded border border-line bg-line/30 px-3 py-2 text-body-sm text-ink-muted";
const errCls = "mt-1 text-caption text-danger";

function emptyToNull(v: string | undefined): string | null {
  if (v === undefined) return null;
  const trimmed = v.trim();
  return trimmed === "" ? null : trimmed;
}

export function ProfilePage() {
  const token = useMemberToken();
  const profile = useMe();
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Values>({
    defaultValues: {
      full_name: "",
      phone: "",
      street: "",
      postal_code: "",
      city: "",
      country: "",
      company_name: "",
      marketing_opt_in: false,
    },
  });

  useEffect(() => {
    if (profile.data) {
      reset({
        full_name: profile.data.full_name,
        phone: profile.data.phone ?? "",
        street: profile.data.street ?? "",
        postal_code: profile.data.postal_code ?? "",
        city: profile.data.city ?? "",
        country: profile.data.country ?? "",
        company_name: profile.data.company_name ?? "",
        marketing_opt_in: profile.data.marketing_opt_in,
      });
    }
  }, [profile.data, reset]);

  const mutation = useMutation<MemberProfile, ApiError, Values>({
    mutationFn: (values) => {
      const parsed = schema.parse(values);
      const payload: MemberUpdateRequest = {
        full_name: parsed.full_name,
        phone: emptyToNull(parsed.phone) ?? undefined,
        street: emptyToNull(parsed.street),
        postal_code: emptyToNull(parsed.postal_code),
        city: emptyToNull(parsed.city),
        country: emptyToNull(parsed.country)?.toUpperCase() ?? null,
        company_name: emptyToNull(parsed.company_name),
        marketing_opt_in: parsed.marketing_opt_in,
      };
      return memberApi.updateMe(payload);
    },
    onSuccess: (updated) => {
      qc.setQueryData(["member", "me"], updated);
    },
  });

  if (!token) {
    return <Navigate to="/konto/login?next=/konto/profil" replace />;
  }

  if (profile.isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center text-body-sm text-ink-muted">
        Profil wird geladen …
      </div>
    );
  }

  if (profile.error || !profile.data) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <p className="mb-3 text-body text-danger">
          Profil konnte nicht geladen werden.
        </p>
        <Link to="/konto/login" className="text-primary underline">
          Erneut anmelden
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <h1 className="mb-2 font-display text-h1 uppercase text-primary">Mein Profil</h1>
      <p className="mb-8 text-body text-ink-soft">
        Diese Daten werden bei neuen Buchungen automatisch übernommen. Adresse und
        Firma sind optional und werden für Rechnungen genutzt.
      </p>

      <form
        onSubmit={handleSubmit((v) => mutation.mutate(v))}
        className="space-y-5"
        noValidate
      >
        <div>
          <label htmlFor="email" className={labelCls}>
            Email
          </label>
          <input
            id="email"
            type="email"
            value={profile.data.email}
            readOnly
            className={readOnlyCls}
          />
          <p className="mt-1 text-caption text-ink-muted">
            Email-Änderung bitte über{" "}
            <a href="mailto:info@tanzraum.koeln" className="text-primary underline">
              info@tanzraum.koeln
            </a>
            .
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="full_name" className={labelCls}>
              Vollständiger Name
            </label>
            <input
              id="full_name"
              autoComplete="name"
              {...register("full_name")}
              className={fieldCls}
            />
            {errors.full_name && <p className={errCls}>{errors.full_name.message}</p>}
          </div>

          <div>
            <label htmlFor="phone" className={labelCls}>
              Telefon <span className="text-ink-muted">(optional)</span>
            </label>
            <input
              id="phone"
              type="tel"
              autoComplete="tel"
              {...register("phone")}
              className={fieldCls}
            />
            {errors.phone && <p className={errCls}>{errors.phone.message}</p>}
          </div>
        </div>

        <fieldset className="space-y-5 rounded-card border border-line bg-surface p-5">
          <legend className="px-2 text-caption font-medium uppercase tracking-widest text-ink-muted">
            Rechnungs-Adresse (optional)
          </legend>

          <div>
            <label htmlFor="company_name" className={labelCls}>
              Firma / Verein
            </label>
            <input
              id="company_name"
              autoComplete="organization"
              {...register("company_name")}
              className={fieldCls}
            />
          </div>

          <div>
            <label htmlFor="street" className={labelCls}>
              Straße &amp; Hausnummer
            </label>
            <input
              id="street"
              autoComplete="street-address"
              {...register("street")}
              className={fieldCls}
            />
          </div>

          <div className="grid grid-cols-3 gap-5">
            <div>
              <label htmlFor="postal_code" className={labelCls}>
                PLZ
              </label>
              <input
                id="postal_code"
                autoComplete="postal-code"
                {...register("postal_code")}
                className={fieldCls}
              />
            </div>
            <div className="col-span-2">
              <label htmlFor="city" className={labelCls}>
                Stadt
              </label>
              <input
                id="city"
                autoComplete="address-level2"
                {...register("city")}
                className={fieldCls}
              />
            </div>
          </div>

          <div>
            <label htmlFor="country" className={labelCls}>
              Land (ISO-2)
            </label>
            <input
              id="country"
              autoComplete="country"
              maxLength={2}
              placeholder="DE"
              {...register("country")}
              className={`${fieldCls} uppercase`}
            />
            {errors.country && <p className={errCls}>{errors.country.message}</p>}
          </div>
        </fieldset>

        <label className="flex items-start gap-3 rounded-card border border-line bg-surface p-4 text-body-sm text-ink-soft">
          <input
            type="checkbox"
            {...register("marketing_opt_in")}
            className="mt-1 h-4 w-4 rounded border-line text-primary focus:ring-primary"
          />
          <span>
            Ich möchte gelegentlich Infos zu neuen Slots und Tanzraum-Angeboten
            erhalten. Jederzeit abbestellbar.
          </span>
        </label>

        {mutation.error && (
          <div className="rounded-card border border-danger bg-danger/5 p-3 text-body-sm text-danger">
            {mutation.error.message}
          </div>
        )}

        {mutation.isSuccess && !isDirty && (
          <div className="rounded-card border border-success bg-success/5 p-3 text-body-sm text-success">
            Profil gespeichert.
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            size="lg"
            loading={isSubmitting || mutation.isPending}
            disabled={!isDirty}
          >
            Speichern
          </Button>
          <Link to="/konto/buchungen" className="text-body-sm text-ink-soft underline">
            Zu meinen Buchungen
          </Link>
        </div>
      </form>
    </div>
  );
}
